import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import { useEffect, useState, useContext } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { toast } from "../../../../../core/helpers/message-helper";
import {
    crearOEditarPersona,
    obtenerDatosCreacionPersonas,
} from "../../../services/personas-service";
import {
    defaultValueInput,
    defaultValueSelect,
    mapToSelect,
} from "../../../../../core/helpers/form-helper";
import Wizard from "../../../../../core/ui/components/Wizard";
import GeneralTab from "./general-tab";
import AreasTab from "./areas-tab";
import SubAreasContext from "../../../store/sub-areas-context";
import InformacionComplementariaTab from "./informacion-complementaria-tab";

const tabFields = {
    0: [
        "id",
        "areas",
        "email",
        "pais_id",
        "nombres",
        "telefono",
        "apellidos",
        "ciudad_id",
        "documento",
        "direccion",
        "fecha_nace",
        "departamento_id",
        "parametro_genero_id",
        "parametro_tipo_documento_id",
    ],
    1: [
        "usuario",
        "entidad_salud_id",
        "personas_a_cargo",
        "parametro_estrato_id",
        "parametro_estado_civil_id",
        "parametro_nivel_educativo_id",
    ],
};

const FormPersonas = ({ modalState, onSaved }) => {
    const { persona } = modalState.data;
    const { personaHasSubAreas, limpiarSubAreas, consultarPersonaHasSubArear } =
        useContext(SubAreasContext);

    const [selects, setSelects] = useState({
        paises: [],
        generos: [],
        tiposSangre: [],
        entidadesSalud: [],
        estadosCiviles: [],
        tiposDocumentos: [],
        estratosSociales: [],
        nivelesEducativos: [],
    });

    function getFormValues() {
        return {
            id: defaultValueInput(persona.id),
            sedes_areas:
                persona.sedes_areas?.map((sa) => ({
                    label: `${sa.nombre_area} (${sa.nombre_sede})`,
                    value: sa.id,
                })) ?? [],
            email: defaultValueInput(persona.email_persona),
            pais_id: defaultValueSelect(persona.pais, "nombre", "id"),
            nombres: defaultValueInput(persona.nombres),
            telefono: defaultValueInput(persona.telefono),
            apellidos: defaultValueInput(persona.apellidos),
            ciudad_id: defaultValueSelect(persona.ciudad, "nombre", "id"),
            documento: defaultValueInput(persona.documento_persona),
            direccion: defaultValueInput(persona.direccion),
            fecha_nace: defaultValueInput(persona.fecha_nace),
            fecha_ingreso: defaultValueInput(persona.fecha_ingreso),
            departamento_id: defaultValueSelect(
                persona.departamento,
                "nombre",
                "id"
            ),
            parametro_tipo_sangre_id: defaultValueSelect(
                persona.tipo_sangre,
                "nombre",
                "id"
            ),
            parametro_genero_id: defaultValueSelect(
                persona.genero,
                "nombre",
                "id"
            ),
            expedicion_documento: defaultValueInput(
                persona.expedicion_documento
            ),
            parametro_tipo_documento_id: defaultValueSelect(
                persona.documento,
                "nombre",
                "id"
            ),
            usuario: defaultValueInput(persona.usuario),
            entidad_salud_id: defaultValueSelect(
                persona.entidad_salud,
                "nombre",
                "id"
            ),
            personas_a_cargo: defaultValueInput(persona.personas_a_cargo),
            parametro_estrato_id: defaultValueSelect(
                persona.estrato,
                "nombre",
                "id"
            ),
            parametro_estado_civil_id: defaultValueSelect(
                persona.estado_civil,
                "nombre",
                "id"
            ),
            parametro_nivel_educativo_id: defaultValueSelect(
                persona.nivel_educativo,
                "nombre",
                "id"
            ),
        };
    }

    const {
        reset,
        control,
        setValue,
        getValues,
        trigger,
        handleSubmit,
        formState: { errors },
    } = useForm();

    useEffect(() => {
        const formValues = getFormValues();
        for (const key in formValues) setValue(key, formValues[key]);
        if (persona.id) consultarPersonaHasSubArear(persona.id);
    }, [persona]);

    const onCreatePersona = async (persona) => {
        const nuevasSubAreas = Array.from(
            personaHasSubAreas.get("nuevas").entries()
        ).map(([_, personaHSA]) => personaHSA.sub_area_has_sede_id);

        const subAreasAEliminar = Array.from(
            personaHasSubAreas.get("aEliminar").entries()
        ).map(([_, personaHSA]) => personaHSA.id);

        if (
            nuevasSubAreas.length == 0 &&
            personaHasSubAreas.get("actuales").size == 0
        ) {
            return toast.error(
                "La persona debe tener como mínimo 1 sub área asignada"
            );
        }

        const resp = await crearOEditarPersona({
            ...persona,
            sedes_areas: persona.sedes_areas.map((sa) => sa.value),
            parametro_tipo_documento_id:
                persona.parametro_tipo_documento_id.value,
            parametro_genero_id: persona.parametro_genero_id.value,
            ciudad_id: persona.ciudad_id.value,
            entidad_salud_id: persona.entidad_salud_id.value,
            parametro_estado_civil_id: persona.parametro_estado_civil_id.value,
            parametro_estrato_id: persona.parametro_estrato_id.value,
            parametro_nivel_educativo_id:
                persona.parametro_nivel_educativo_id.value,
            parametro_tipo_sangre_id: persona.parametro_tipo_sangre_id.value,
            nuevasSubAreas,
            subAreasAEliminar,
        });

        if (!resp.status) return toast.error(resp.message);
        limpiarSubAreas();
        onSaved();
    };

    useEffect(async () => {
        const resp = await obtenerDatosCreacionPersonas();
        if (!resp.status) return toast.error(resp.message);
        const { data } = resp;

        setSelects({
            paises: data.paises.map((p) => mapToSelect(p.id, p.nombre)),
            sedesAreas: data.sedes_areas.map((sa) =>
                mapToSelect(sa.id, `${sa.nombre_area} (${sa.nombre_sede})`)
            ),
            generos: data.generos.map((g) => mapToSelect(g.id, g.nombre)),
            tiposSangre: data.tipos_sangre.map((ts) =>
                mapToSelect(ts.id, ts.nombre)
            ),
            entidadesSalud: data.entidades_salud.map((es) =>
                mapToSelect(es.id, es.nombre)
            ),
            estadosCiviles: data.estados_civiles.map((ec) =>
                mapToSelect(ec.id, ec.nombre)
            ),
            tiposDocumentos: data.tipos_documentos.map((td) =>
                mapToSelect(td.id, td.nombre)
            ),
            estratosSociales: data.estratos_sociales.map((es) =>
                mapToSelect(es.id, es.nombre)
            ),
            nivelesEducativos: data.niveles_educativos.map((ne) =>
                mapToSelect(ne.id, ne.nombre)
            ),
        });
    }, []);

    async function onNextTab(activeStep) {
        return await trigger(tabFields[activeStep]);
    }

    const tabs = [
        {
            nombre: "General",
            descripcion: "Información básica de la persona",
            icono: "fas fa-plus",
            componente: (
                <GeneralTab
                    control={control}
                    errors={errors}
                    selects={selects}
                    persona={persona}
                    defaultValues={getFormValues()}
                    setValue={setValue}
                />
            ),
        },
        {
            nombre: "Información complementaria",
            descripcion: "Información complementaria del perfíl de la persona",
            icono: "fas fa-plus",
            componente: (
                <InformacionComplementariaTab
                    control={control}
                    errors={errors}
                    selects={selects}
                />
            ),
        },
        {
            nombre: "Areas",
            descripcion: "Areas a las cuales pertenece la persona",
            icono: "fas fa-plus",
            componente: (
                <AreasTab
                    errors={errors}
                    control={control}
                    selects={selects}
                    getValues={getValues}
                />
            ),
        },
    ];

    return (
        <Modal
            centered
            size={"xl"}
            isOpen={modalState.show}
            aria-labelledby="contained-modal-title-vcenter"
        >
            <ModalHeader className="bg-secondary text-white text-bold">
                {modalState.title}
            </ModalHeader>
            <ModalBody>
                <form onSubmit={handleSubmit(onCreatePersona)}>
                    <Wizard tabs={tabs} onNextTab={onNextTab} />
                </form>
            </ModalBody>
            <ModalFooter>
                <div>
                    <Button
                        key="close"
                        color="secondary"
                        onClick={() => {
                            reset();
                            limpiarSubAreas();
                            modalState.close();
                        }}
                    >
                        <i className="fas fa-trash mr-2"></i>
                        Cancelar
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
};

FormPersonas.propTypes = {
    modalState: PropTypes.object,
    onSaved: PropTypes.func,
};

export default FormPersonas;
